import { createStore } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { QuarantineStore } from './types';
import { createRiskAiState } from './riskAiSlice';
import { immer } from 'zustand/middleware/immer';
import _merge from 'lodash/merge';
import i18nInst from 'src/i18n';

export interface QuarantineStoreProps {
  orgId: string;
  utcOffset: string;
}

export const createQuarantineStore = (initProps: QuarantineStoreProps) => {
  const { t } = i18nInst;
  const currentOrgId = initProps?.orgId || 'all';
  const currentUtcOffset = initProps?.utcOffset || '+00:00';
  return createStore<QuarantineStore>()(
    devtools(
      // Store is persisted to local storage, update the version number when breaking changes are made
      persist(
        immer((set, get, store) => {
          return {
            t,
            currentOrgId,
            currentUtcOffset,
            setOrgId: (orgId: string) => {
              set({ currentOrgId: orgId });
            },
            setUtcOffset: (utcOffset: string) => {
              set({ currentUtcOffset: utcOffset });
            },
            riskAi: createRiskAiState({ set, get, store, currentUtcOffset }),
            onHydrate: () => {
              get().riskAi.onHydrate();
            },
          };
        }),
        {
          name: 'quarantineStore',
          version: 11, // IMPORTANT: Increment this when you change the store elements that were partialized (breaking changes)
          partialize: (state) => {
            return {
              riskAi: {
                securityRiskColumnConfig: state.riskAi.securityRiskColumnConfig,
                anomalyColumnConfig: state.riskAi.anomalyColumnConfig,
                customizedAnomalyColumnConfig:
                  state.riskAi.customizedAnomalyColumnConfig,
                filters: {
                  tagFilter: {
                    currentValue: state.riskAi.filters.tagFilter.currentValue,
                  },
                  securityRiskNameFilter: {
                    currentValue:
                      state.riskAi.filters.securityRiskNameFilter.currentValue,
                  },
                  anomalyNameFilter: {
                    currentValue:
                      state.riskAi.filters.anomalyNameFilter.currentValue,
                  },
                  detectedByFilter: {
                    currentValue:
                      state.riskAi.filters.detectedByFilter.currentValue,
                  },
                  timeFilter: {
                    currentValue: state.riskAi.filters.timeFilter.currentValue,
                    custom: state.riskAi.filters.timeFilter.custom,
                  },
                  serviceFilter: {
                    currentValue:
                      state.riskAi.filters.serviceFilter.currentValue,
                  },
                },
              },
            };
          },
          onRehydrateStorage: () => (state) => {
            state?.onHydrate();
          },
          merge: (persistedState, currentState) =>
            _merge(currentState, persistedState),
        }
      )
    )
  );
};

export type QuarantineStoreApi = ReturnType<typeof createQuarantineStore>;
